import React, { useEffect, useState, useCallback } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { ujmsFeeSchedule, ujmsSubmitEnvelope, ujmsEnvelopes, ujmsEnvelopeDetail, ujmsReviewEnvelope,
  ujmsSearchCases, ujmsWorkspace, ujmsOpenDocument } from "../../services/ujmsApi";
import { Panel, Badge, thCls, tdCls, fmtDT, btnPrimary, btnGhost, btnDanger, inputCls, selectCls, Field, can, cls } from "../ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Send, FileText, Trash2, Plus, CreditCard } from "lucide-react";
import { uploadFileChunked } from "../../services/upload";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function EFile() {
  const { access } = useOutletContext();
  const clerk = can(access, "filing.accept");
  const filer = can(access, "efile.submit");
  const [envs, setEnvs] = useState([]);
  const [statusF, setStatusF] = useState(clerk ? "SUBMITTED" : "");
  const [wizard, setWizard] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => {
    ujmsEnvelopes(statusF ? { status: statusF } : {}).then((r) => setEnvs(r.data)).catch(() => {});
  }, [statusF]);
  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    try { setDetail((await ujmsEnvelopeDetail(id)).data); } catch (e) { toast.error("Failed to load envelope"); }
  };

  return (
    <div className="space-y-5" data-testid="efile-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">{clerk ? "eFile & Serve — Envelope Review" : "eFile & Serve"}</h1>
          <p className="text-sm text-slate-500">
            {clerk ? "Review submitted filing envelopes: accept to file-stamp, collect fees and complete e-service, or reject with a reason."
              : "File documents electronically: build an envelope, serve opposing counsel, and pay filing fees in one step."}
          </p>
        </div>
        {filer && (
          <button className={btnPrimary} onClick={() => setWizard(true)} data-testid="efile-new-envelope-btn">
            <Send className="w-4 h-4" /> New Envelope
          </button>
        )}
      </div>

      <div className="flex items-center gap-2">
        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Status</label>
        <select className={`${selectCls} w-44`} value={statusF} onChange={(e) => setStatusF(e.target.value)} data-testid="efile-status-filter">
          <option value="">All</option>
          <option value="SUBMITTED">Submitted</option>
          <option value="ACCEPTED">Accepted</option>
          <option value="REJECTED">Rejected</option>
        </select>
      </div>

      <Panel title={clerk ? "Envelopes" : "My envelopes"} testId="efile-list-panel">
        <table className="w-full border-collapse">
          <thead><tr><th className={thCls}>Envelope</th><th className={thCls}>Case</th><th className={thCls}>Filings</th>
            <th className={thCls}>Fees</th><th className={thCls}>Filed By</th><th className={thCls}>Submitted</th><th className={thCls}>Status</th><th className={thCls}></th></tr></thead>
          <tbody>
            {envs.map((e) => (
              <tr key={e.id} className="hover:bg-slate-50">
                <td className={`${tdCls} font-mono text-xs font-bold text-ujms`}>{e.envelope_number}</td>
                <td className={`${tdCls} font-mono text-xs`}><Link className="text-blue-900 hover:underline" to={`/ujms/cases/${e.case_id}`}>{e.case_number}</Link>
                  <div className="text-slate-400 truncate max-w-[220px]">{e.caption}</div></td>
                <td className={tdCls}>{e.filings?.length}</td>
                <td className={`${tdCls} font-mono text-xs`}>{money(e.fees?.total)}</td>
                <td className={`${tdCls} text-xs`}>{e.filer_name}</td>
                <td className={`${tdCls} font-mono text-xs`}>{fmtDT(e.submitted_at)}</td>
                <td className={tdCls}><Badge value={e.status} />
                  {e.status === "REJECTED" && e.rejection_reason && <div className="text-[11px] text-red-700 max-w-[180px]">{e.rejection_reason}</div>}</td>
                <td className={tdCls}><button className={btnGhost} onClick={() => openDetail(e.id)} data-testid={`efile-open-${e.id}`}>{clerk && e.status === "SUBMITTED" ? "Review" : "View"}</button></td>
              </tr>
            ))}
            {!envs.length && <tr><td colSpan={8} className={tdCls} data-testid="efile-empty">{filer && !clerk ? "No envelopes yet — click New Envelope to e-file into a case." : "No envelopes match this filter."}</td></tr>}
          </tbody>
        </table>
      </Panel>

      <EnvelopeWizard open={wizard} onClose={() => setWizard(false)} onDone={load} />
      <EnvelopeDetail env={detail} clerk={clerk} onClose={() => setDetail(null)} onDone={() => { setDetail(null); load(); }} />
    </div>
  );
}

function EnvelopeWizard({ open, onClose, onDone }) {
  const [fees, setFees] = useState(null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [c, setC] = useState(null);
  const [parties, setParties] = useState([]);
  const [items, setItems] = useState([]);
  const [serve, setServe] = useState({});
  const [payer, setPayer] = useState("");
  const [card, setCard] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setC(null); setItems([{ _k: "0", filing_type: "Motion", title: "", content_b64: null, filename: "" }]);
      setServe({}); setQ(""); setResults([]); setCard(""); setParties([]);
      ujmsFeeSchedule().then((r) => setFees(r.data)).catch(() => {});
    }
  }, [open]);

  const search = async () => {
    if (!q.trim()) return;
    try { const r = await ujmsSearchCases({ q, page_size: 6 }); setResults(r.data.items || []); } catch (e) { /* non-blocking */ }
  };
  const pickCase = async (cs) => {
    setResults([]);
    try { const r = await ujmsWorkspace(cs.id); setParties(r.data.parties || []); } catch (e) { setParties([]); }
    setC(cs);
  };
  const setItem = (i, patch) => setItems((xs) => xs.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const onFile = async (i, file) => {
    if (!file) return;
    try { const rec = await uploadFileChunked(file, { purpose: "DOCUMENT" }); setItem(i, { upload_id: rec.upload_id, content_b64: null, filename: file.name }); }
    catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
  };
  const feeFor = (t) => fees?.types?.find((x) => x.name === t)?.fee ?? 50;
  const total = items.reduce((s, it) => s + feeFor(it.filing_type), 0) + (fees?.convenience_fee || 0);
  const valid = c && items.length && items.every((it) => it.title.trim()) && payer.trim();

  const submit = async () => {
    setBusy(true);
    try {
      const r = await ujmsSubmitEnvelope({
        case_id: c.id,
        filings: items.map(({ _k, ...it }) => it),
        service_party_ids: Object.keys(serve).filter((k) => serve[k]),
        payment: { payer_name: payer, card_last4: card.slice(-4) },
      });
      toast.success(`Envelope ${r.data.envelope_number} submitted — ${money(r.data.fees.total)} authorized, awaiting clerk review`);
      onDone(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Submission failed"); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="efile-wizard">
        <DialogHeader><DialogTitle>New Filing Envelope</DialogTitle></DialogHeader>
        <div className="space-y-4 text-sm">
          <Field label="1 — Case" required>
            {c ? (
              <div className="flex items-center gap-2 border border-emerald-300 bg-emerald-50 rounded-sm px-2 py-1.5" data-testid="efile-case-selected">
                <span className="font-mono text-xs font-bold text-emerald-800">{c.case_number}</span>
                <span className="text-xs text-slate-600 truncate">{c.caption}</span>
                <button className="ml-auto text-xs text-blue-900 underline" onClick={() => setC(null)} data-testid="efile-case-change">Change</button>
              </div>
            ) : (
              <div className="space-y-1.5">
                <div className="flex gap-1.5">
                  <input className={inputCls} placeholder="Search case number or party…" value={q}
                    onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} data-testid="efile-case-search" />
                  <button className={btnGhost} onClick={search} data-testid="efile-case-search-btn">Search</button>
                </div>
                {results.map((cs) => (
                  <div key={cs.id} className="flex items-center gap-2 px-2 py-1.5 border border-slate-200 rounded-sm hover:bg-blue-50 cursor-pointer"
                    onClick={() => pickCase(cs)} data-testid={`efile-case-pick-${cs.case_number}`}>
                    <span className="font-mono text-xs font-bold text-ujms">{cs.case_number}</span>
                    <span className="text-xs truncate">{cs.caption}</span>
                  </div>
                ))}
              </div>
            )}
          </Field>

          <Field label="2 — Filings (PDF documents)" required>
            <div className="space-y-2">
              {items.map((it, i) => (
                <div key={it._k} className="flex items-end gap-2 border border-slate-200 rounded-sm p-2">
                  <Field label="Type" className="w-44">
                    <select className={selectCls} value={it.filing_type} onChange={(e) => setItem(i, { filing_type: e.target.value })} data-testid={`efile-filing-type-${i}`}>
                      {(fees?.types || []).map((t) => <option key={t.name} value={t.name}>{`${t.name} — ${money(t.fee)}`}</option>)}
                    </select>
                  </Field>
                  <Field label="Document title" className="flex-1">
                    <input className={inputCls} value={it.title} onChange={(e) => setItem(i, { title: e.target.value })}
                      placeholder="e.g. Motion to Compel Discovery" data-testid={`efile-filing-title-${i}`} />
                  </Field>
                  <Field label="PDF" className="w-48">
                    <input type="file" accept="application/pdf" className="text-xs" onChange={(e) => onFile(i, e.target.files?.[0])} data-testid={`efile-filing-file-${i}`} />
                  </Field>
                  <span className="font-mono text-xs pb-2 w-14 text-right">{money(feeFor(it.filing_type))}</span>
                  {items.length > 1 && (
                    <button className="text-slate-400 hover:text-red-700 pb-2" onClick={() => setItems((xs) => xs.filter((_, j) => j !== i))} data-testid={`efile-filing-remove-${i}`}>
                      <Trash2 className="w-4 h-4" /></button>)}
                </div>
              ))}
              <button className={btnGhost} onClick={() => setItems((xs) => [...xs, { _k: Math.random().toString(36).slice(2), filing_type: "Motion", title: "", content_b64: null, filename: "" }])}
                data-testid="efile-add-filing"><Plus className="w-3.5 h-3.5" /> Add Filing</button>
            </div>
          </Field>

          <Field label="3 — Serve (electronic service on acceptance)">
            <div className="space-y-1">
              {parties.map((p) => (
                <label key={p.id} className="flex items-center gap-2 cursor-pointer text-sm">
                  <input type="checkbox" checked={!!serve[p.id]} onChange={(e) => setServe((s) => ({ ...s, [p.id]: e.target.checked }))}
                    data-testid={`efile-serve-${p.id}`} />
                  <span>{p.display_name}</span>
                  {p.attorney_name && <span className="text-xs text-slate-400">· counsel: {p.attorney_name}</span>}
                </label>
              ))}
              {c && !parties.length && <div className="text-xs text-slate-400">No parties on record.</div>}
              {!c && !parties.length && <div className="text-xs text-slate-400">Select a case to load its parties.</div>}
            </div>
          </Field>

          <Field label="4 — Payment (simulated card)" required>
            <div className="border border-slate-200 rounded-sm p-3 space-y-2">
              {items.map((it, i) => (
                <div key={it._k} className="flex justify-between text-xs"><span>{it.filing_type}{it.title ? ` — ${it.title}` : ""}</span><span className="font-mono">{money(feeFor(it.filing_type))}</span></div>
              ))}
              <div className="flex justify-between text-xs text-slate-500"><span>Convenience fee</span><span className="font-mono">{money(fees?.convenience_fee)}</span></div>
              <div className="flex justify-between font-semibold border-t border-slate-200 pt-1.5" data-testid="efile-total"><span>Total due on acceptance</span><span className="font-mono">{money(total)}</span></div>
              <div className="flex gap-2 pt-1">
                <input className={inputCls} placeholder="Cardholder / payer name" value={payer} onChange={(e) => setPayer(e.target.value)} data-testid="efile-payer" />
                <div className="relative w-52">
                  <CreditCard className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
                  <input className={`${inputCls} pl-8`} placeholder="Card number (demo)" value={card}
                    onChange={(e) => setCard(e.target.value.replace(/[^0-9]/g, "").slice(0, 16))} data-testid="efile-card" />
                </div>
              </div>
              <div className="text-[11px] text-slate-400">SIMULATED payment — the card is authorized now and charged only when the Clerk accepts the envelope. No real payment is processed.</div>
            </div>
          </Field>
        </div>
        <DialogFooter>
          <button className={btnGhost} onClick={onClose}>Cancel</button>
          <button className={btnPrimary} disabled={!valid || busy} onClick={submit} data-testid="efile-submit-btn">
            <Send className="w-4 h-4" /> {busy ? "Submitting…" : `Submit Envelope (${money(total)})`}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EnvelopeDetail({ env, clerk, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setReason(""); }, [env]);
  if (!env) return null;
  const act = async (action) => {
    setBusy(true);
    try {
      const r = await ujmsReviewEnvelope(env.id, { action, reason });
      toast.success(action === "ACCEPT"
        ? `Envelope accepted — filings docketed, ${money(env.fees?.total)} collected (Receipt ${r.data.receipt_number}), e-service completed`
        : "Envelope rejected — filer notified, no fees charged");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Review failed"); }
    setBusy(false);
  };
  return (
    <Dialog open={!!env} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="efile-detail-dialog">
        <DialogHeader><DialogTitle>Envelope {env.envelope_number}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <Badge value={env.status} />
            <span className="font-mono text-xs text-ujms font-bold">{env.case?.case_number || env.case_number}</span>
            <span className="text-xs text-slate-500 truncate">{env.case?.caption}</span>
            <span className="ml-auto text-xs text-slate-400">by {env.filer_name} · {fmtDT(env.submitted_at)}</span>
          </div>
          {env.rejection_reason && <div className="text-xs text-red-700 border border-red-200 bg-red-50 rounded-sm px-2 py-1.5" data-testid="efile-detail-reason">Rejected: {env.rejection_reason}</div>}
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Filings</div>
            {(env.filings || []).map((f) => (
              <div key={f.filing_id} className="flex items-center gap-2 border-b border-slate-100 py-1.5">
                <FileText className="w-4 h-4 text-slate-400" />
                <span className="flex-1">{f.title}<span className="text-xs text-slate-400 ml-1.5">({f.filing_type})</span></span>
                <span className="font-mono text-xs">{money(f.fee)}</span>
                {f.document_id && <button className={btnGhost} onClick={() => ujmsOpenDocument(f.document_id, f.title)} data-testid={`efile-doc-${f.filing_id}`}>View PDF</button>}
              </div>
            ))}
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">E-Service</div>
            {(env.service_contacts || []).length
              ? env.service_contacts.map((s, i) => (
                <div key={`${s.party_id}-${i}`} className="text-xs text-slate-600">{s.name}{s.attorney_name ? ` — ${s.attorney_name}` : ""} <span className="text-slate-400">({env.status === "ACCEPTED" ? "served electronically" : "will be e-served on acceptance"})</span></div>))
              : <div className="text-xs text-slate-400">No service contacts selected.</div>}
          </div>
          <div className="border border-slate-200 rounded-sm p-2.5">
            <div className="flex justify-between text-xs text-slate-500"><span>Filing fees + convenience fee</span>
              <span className="font-mono">{money(env.fees?.total)}</span></div>
            <div className="flex justify-between text-xs mt-1"><span>Payment — card ****{env.payment?.card_last4} ({env.payment?.payer_name})</span>
              <span className={cls("font-mono font-bold", env.payment?.status === "PAID" ? "text-emerald-700" : env.payment?.status === "VOID" ? "text-red-700" : "text-amber-700")} data-testid="efile-detail-paystatus">{env.payment?.status}</span></div>
            {env.receipt_number && <div className="text-xs text-slate-500 mt-1">Receipt <span className="font-mono font-bold">{env.receipt_number}</span></div>}
          </div>
          {clerk && env.status === "SUBMITTED" && (
            <Field label="Rejection reason (required to reject)">
              <input className={inputCls} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="efile-reject-reason" />
            </Field>
          )}
        </div>
        <DialogFooter>
          <button className={btnGhost} onClick={onClose}>Close</button>
          {clerk && env.status === "SUBMITTED" && (<>
            <button className={btnDanger} disabled={busy || !reason.trim()} onClick={() => act("REJECT")} data-testid="efile-reject-btn">Reject</button>
            <button className={btnPrimary} disabled={busy} onClick={() => act("ACCEPT")} data-testid="efile-accept-btn">{busy ? "Processing…" : "Accept, Collect Fees & Serve"}</button>
          </>)}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
